// PFMEA Standards Configuration
// Top 5+ Industry Standard Coverage for Global Manufacturing
// ============================================================

import { PfmeaStandard, CauseCategory } from '../types';

export interface ColumnConfig {
    key: string;
    label: string;
    width: number;
    visible: boolean;
    editable: boolean;
    required?: boolean;
}

export interface StandardConfig {
    id: PfmeaStandard;
    name: string;
    description: string;
    industry: string[];
    useActionPriority: boolean; // True = AP (H/M/L), False = RPN
    columns: ColumnConfig[];
    severityScale: { value: number; description: string }[];
    occurrenceScale: { value: number; description: string }[];
    detectionScale: { value: number; description: string }[];
    causeCategories: CauseCategory[];
    complianceRequirements: string[];
}

// ============================================================
// AIAG-VDA (Automotive Industry Action Group + VDA)
// Primary: Automotive, Tier 1/2 Suppliers
// ============================================================

const AIAG_VDA_CONFIG: StandardConfig = {
    id: 'AIAG_VDA',
    name: 'AIAG-VDA (2019)',
    description: 'AIAG-VDA FMEA Handbook - 7-Step Approach with Action Priority',
    industry: ['Automotive', 'Heavy Equipment', 'Commercial Vehicles'],
    useActionPriority: true,
    columns: [
        { key: 'processStepNumber', label: 'Step #', width: 80, visible: true, editable: true, required: true },
        { key: 'processStepName', label: 'Process Step', width: 180, visible: true, editable: true, required: true },
        { key: 'processElement', label: '4M Category', width: 100, visible: true, editable: true },
        { key: 'processFunction', label: 'Process Function/Requirement', width: 200, visible: true, editable: true, required: true },
        { key: 'failureMode', label: 'Failure Mode', width: 180, visible: true, editable: true, required: true },
        { key: 'failureEffectLocal', label: 'Effect (Local)', width: 150, visible: true, editable: true },
        { key: 'failureEffectNext', label: 'Effect (Next Operation)', width: 150, visible: true, editable: true },
        { key: 'failureEffectEnd', label: 'Effect (End User)', width: 150, visible: true, editable: true, required: true },
        { key: 'severity', label: 'S', width: 50, visible: true, editable: true, required: true },
        { key: 'failureCause', label: 'Failure Cause', width: 180, visible: true, editable: true, required: true },
        { key: 'preventionControls', label: 'Prevention Controls', width: 200, visible: true, editable: true },
        { key: 'occurrence', label: 'O', width: 50, visible: true, editable: true, required: true },
        { key: 'detectionControls', label: 'Detection Controls', width: 200, visible: true, editable: true },
        { key: 'detection', label: 'D', width: 50, visible: true, editable: true, required: true },
        { key: 'actionPriority', label: 'AP', width: 50, visible: true, editable: false },
        { key: 'specialCharacteristic', label: 'SC/CC', width: 60, visible: true, editable: true },
        { key: 'status', label: 'Status', width: 100, visible: true, editable: true },
    ],
    severityScale: [
        { value: 10, description: 'May endanger operator/user - with warning' },
        { value: 9, description: 'May endanger operator/user - without warning' },
        { value: 8, description: 'Loss of primary function - complete' },
        { value: 7, description: 'Loss of primary function - degraded' },
        { value: 6, description: 'Loss of secondary function - complete' },
        { value: 5, description: 'Loss of secondary function - degraded' },
        { value: 4, description: 'Minor impact - noticeable by customers' },
        { value: 3, description: 'Minor impact - noticeable by some customers' },
        { value: 2, description: 'Negligible impact - noticed by discerning customers' },
        { value: 1, description: 'No discernible effect' },
    ],
    occurrenceScale: [
        { value: 10, description: 'Very high - ≥100 per 1000' },
        { value: 9, description: 'High - 50 per 1000' },
        { value: 8, description: 'High - 20 per 1000' },
        { value: 7, description: 'Moderate - 10 per 1000' },
        { value: 6, description: 'Moderate - 2 per 1000' },
        { value: 5, description: 'Low - 0.5 per 1000' },
        { value: 4, description: 'Low - 0.1 per 1000' },
        { value: 3, description: 'Very low - 0.01 per 1000' },
        { value: 2, description: 'Remote - ≤0.001 per 1000' },
        { value: 1, description: 'Almost impossible - controlled process' },
    ],
    detectionScale: [
        { value: 10, description: 'No detection opportunity' },
        { value: 9, description: 'Very remote chance of detection' },
        { value: 8, description: 'Remote - detection after passing station' },
        { value: 7, description: 'Very low - detection at next station' },
        { value: 6, description: 'Low - detection at the station' },
        { value: 5, description: 'Moderate - automated detection' },
        { value: 4, description: 'Moderately high - error-proofing' },
        { value: 3, description: 'High - in-station detection' },
        { value: 2, description: 'Very high - immediate detection' },
        { value: 1, description: 'Almost certain - mistake-proofing' },
    ],
    causeCategories: ['Man', 'Machine', 'Material', 'Method'],
    complianceRequirements: ['IATF 16949', 'VDA 6.3', 'VDA 6.5'],
};

// ============================================================
// AIAG V4 (Traditional Automotive)
// Primary: North American Automotive
// ============================================================

const AIAG_V4_CONFIG: StandardConfig = {
    id: 'AIAG_V4',
    name: 'AIAG FMEA V4',
    description: 'AIAG FMEA Manual 4th Edition - Traditional RPN Approach',
    industry: ['Automotive', 'Heavy Equipment'],
    useActionPriority: false,
    columns: [
        { key: 'processStepNumber', label: 'Operation', width: 80, visible: true, editable: true, required: true },
        { key: 'processStepName', label: 'Process Step', width: 180, visible: true, editable: true, required: true },
        { key: 'processFunction', label: 'Function/Requirement', width: 200, visible: true, editable: true, required: true },
        { key: 'failureMode', label: 'Potential Failure Mode', width: 180, visible: true, editable: true, required: true },
        { key: 'failureEffectEnd', label: 'Potential Effect(s)', width: 200, visible: true, editable: true, required: true },
        { key: 'severity', label: 'SEV', width: 50, visible: true, editable: true, required: true },
        { key: 'specialCharacteristic', label: 'Class', width: 60, visible: true, editable: true },
        { key: 'failureCause', label: 'Potential Cause(s)', width: 180, visible: true, editable: true, required: true },
        { key: 'preventionControls', label: 'Current Controls (Prevention)', width: 180, visible: true, editable: true },
        { key: 'occurrence', label: 'OCC', width: 50, visible: true, editable: true, required: true },
        { key: 'detectionControls', label: 'Current Controls (Detection)', width: 180, visible: true, editable: true },
        { key: 'detection', label: 'DET', width: 50, visible: true, editable: true, required: true },
        { key: 'rpn', label: 'RPN', width: 60, visible: true, editable: false },
        { key: 'status', label: 'Status', width: 100, visible: true, editable: true },
    ],
    severityScale: [
        { value: 10, description: 'Hazardous without warning' },
        { value: 9, description: 'Hazardous with warning' },
        { value: 8, description: 'Very high - inoperable' },
        { value: 7, description: 'High - reduced performance' },
        { value: 6, description: 'Moderate - item operable, comfort affected' },
        { value: 5, description: 'Low - reduced comfort' },
        { value: 4, description: 'Very low - fit and finish issues' },
        { value: 3, description: 'Minor - slight inconvenience' },
        { value: 2, description: 'Very minor - discerning customer notices' },
        { value: 1, description: 'None' },
    ],
    occurrenceScale: [
        { value: 10, description: 'Very high - ≥1 in 2' },
        { value: 9, description: 'High - 1 in 3' },
        { value: 8, description: 'High - 1 in 8' },
        { value: 7, description: 'Moderate - 1 in 20' },
        { value: 6, description: 'Moderate - 1 in 80' },
        { value: 5, description: 'Low - 1 in 400' },
        { value: 4, description: 'Low - 1 in 2,000' },
        { value: 3, description: 'Very low - 1 in 15,000' },
        { value: 2, description: 'Remote - 1 in 150,000' },
        { value: 1, description: 'Almost impossible - 1 in 1,500,000' },
    ],
    detectionScale: [
        { value: 10, description: 'Absolute uncertainty - no control' },
        { value: 9, description: 'Very remote - indirect detection' },
        { value: 8, description: 'Remote - inspection after setup' },
        { value: 7, description: 'Very low - double visual inspection' },
        { value: 6, description: 'Low - charting methods like SPC' },
        { value: 5, description: 'Moderate - gauging after processing' },
        { value: 4, description: 'Moderately high - error detection' },
        { value: 3, description: 'High - automated detection' },
        { value: 2, description: 'Very high - immediate detection' },
        { value: 1, description: 'Almost certain - poka-yoke' },
    ],
    causeCategories: ['Man', 'Machine', 'Material', 'Method'],
    complianceRequirements: ['IATF 16949', 'QS-9000'],
};

// ============================================================
// ISO 14971 (Medical Devices)
// Primary: Medical Device Manufacturers
// ============================================================

const ISO_14971_CONFIG: StandardConfig = {
    id: 'ISO_14971',
    name: 'ISO 14971 Risk Management',
    description: 'Medical Device Risk Management - Hazard Analysis with FDA/EU MDR compliance',
    industry: ['Medical Devices', 'Pharmaceuticals', 'Healthcare'],
    useActionPriority: true,
    columns: [
        { key: 'processStepNumber', label: 'Process ID', width: 80, visible: true, editable: true, required: true },
        { key: 'processStepName', label: 'Process Step', width: 180, visible: true, editable: true, required: true },
        { key: 'processFunction', label: 'Intended Function', width: 200, visible: true, editable: true, required: true },
        { key: 'hazard', label: 'Hazard', width: 150, visible: true, editable: true, required: true },
        { key: 'failureMode', label: 'Hazardous Situation', width: 180, visible: true, editable: true, required: true },
        { key: 'harm', label: 'Harm', width: 150, visible: true, editable: true, required: true },
        { key: 'severity', label: 'Severity', width: 50, visible: true, editable: true, required: true },
        { key: 'occurrence', label: 'P1×P2', width: 60, visible: true, editable: true, required: true },
        { key: 'riskLevel', label: 'Risk Level', width: 80, visible: true, editable: false },
        { key: 'riskControl', label: 'Risk Control Measures', width: 200, visible: true, editable: true },
        { key: 'residualRisk', label: 'Residual Risk', width: 80, visible: true, editable: true },
        { key: 'benefitRiskRatio', label: 'B/R Ratio', width: 80, visible: true, editable: true },
        { key: 'status', label: 'Status', width: 100, visible: true, editable: true },
    ],
    severityScale: [
        { value: 5, description: 'Catastrophic - Death or permanent impairment' },
        { value: 4, description: 'Critical - Reversible serious injury' },
        { value: 3, description: 'Serious - Medical intervention required' },
        { value: 2, description: 'Minor - Temporary discomfort' },
        { value: 1, description: 'Negligible - No injury' },
    ],
    occurrenceScale: [
        { value: 5, description: 'Frequent - Likely to occur often' },
        { value: 4, description: 'Probable - Expected to occur' },
        { value: 3, description: 'Occasional - May occur sometime' },
        { value: 2, description: 'Remote - Unlikely but possible' },
        { value: 1, description: 'Improbable - Very unlikely' },
    ],
    detectionScale: [
        { value: 5, description: 'Undetectable - No detection possible' },
        { value: 4, description: 'Low - May be detected after harm' },
        { value: 3, description: 'Moderate - May be detected before harm' },
        { value: 2, description: 'High - Likely to be detected' },
        { value: 1, description: 'Certain - Will be detected by design' },
    ],
    causeCategories: ['User Error', 'Device Failure', 'Environmental', 'Manufacturing'],
    complianceRequirements: ['ISO 14971', 'FDA 21 CFR 820', 'EU MDR 2017/745', 'IEC 62366'],
};

// ============================================================
// AS9100D / SAE ARP5580 (Aerospace)
// Primary: Aerospace & Defense
// ============================================================

const AS9100D_CONFIG: StandardConfig = {
    id: 'AS9100D',
    name: 'AS9100D / ARP5580',
    description: 'Aerospace Quality Management with SAE ARP5580 FMECA',
    industry: ['Aerospace', 'Defense', 'Aviation'],
    useActionPriority: false,
    columns: [
        { key: 'processStepNumber', label: 'Process Ref', width: 80, visible: true, editable: true, required: true },
        { key: 'processStepName', label: 'Process Operation', width: 180, visible: true, editable: true, required: true },
        { key: 'processFunction', label: 'Function', width: 200, visible: true, editable: true, required: true },
        { key: 'failureMode', label: 'Failure Mode', width: 180, visible: true, editable: true, required: true },
        { key: 'failureMechanism', label: 'Failure Mechanism', width: 150, visible: true, editable: true },
        { key: 'failureEffectEnd', label: 'Local/Next/End Effect', width: 200, visible: true, editable: true, required: true },
        { key: 'severity', label: 'Sev', width: 50, visible: true, editable: true, required: true },
        { key: 'failureCause', label: 'Failure Cause', width: 180, visible: true, editable: true, required: true },
        { key: 'occurrence', label: 'Prob', width: 50, visible: true, editable: true, required: true },
        { key: 'detection', label: 'Det', width: 50, visible: true, editable: true, required: true },
        { key: 'criticality', label: 'Cm', width: 60, visible: true, editable: false },
        { key: 'rpn', label: 'RPN', width: 60, visible: true, editable: false },
        { key: 'criticalItem', label: 'Critical Item', width: 80, visible: true, editable: true },
        { key: 'compensatingProvision', label: 'Compensating Provision', width: 180, visible: true, editable: true },
        { key: 'status', label: 'Status', width: 100, visible: true, editable: true },
    ],
    severityScale: [
        { value: 4, description: 'Catastrophic - Loss of life, major system' },
        { value: 3, description: 'Critical - Severe injury, major damage' },
        { value: 2, description: 'Marginal - Minor injury, degradation' },
        { value: 1, description: 'Minor - No injury, minor degradation' },
    ],
    occurrenceScale: [
        { value: 5, description: 'Frequent - >0.20' },
        { value: 4, description: 'Reasonably probable - 0.10-0.20' },
        { value: 3, description: 'Occasional - 0.01-0.10' },
        { value: 2, description: 'Remote - 0.001-0.01' },
        { value: 1, description: 'Extremely unlikely - <0.001' },
    ],
    detectionScale: [
        { value: 5, description: 'Undetectable' },
        { value: 4, description: 'Low probability' },
        { value: 3, description: 'Moderate probability' },
        { value: 2, description: 'High probability' },
        { value: 1, description: 'Almost certain' },
    ],
    causeCategories: ['Design', 'Manufacturing', 'Material', 'Maintenance', 'Human Factors'],
    complianceRequirements: ['AS9100D', 'SAE ARP5580', 'MIL-STD-1629A', 'FAA AC 25.1309'],
};

// ============================================================
// MIL-STD-1629A (Military/Defense)
// Primary: Defense Contractors
// ============================================================

const MIL_STD_1629A_CONFIG: StandardConfig = {
    id: 'MIL_STD_1629A',
    name: 'MIL-STD-1629A FMECA',
    description: 'Military Critical Failure Modes, Effects, and Criticality Analysis',
    industry: ['Military', 'Defense', 'Government'],
    useActionPriority: false,
    columns: [
        { key: 'processStepNumber', label: 'Item ID', width: 80, visible: true, editable: true, required: true },
        { key: 'processStepName', label: 'Item/Process', width: 180, visible: true, editable: true, required: true },
        { key: 'processFunction', label: 'Function', width: 200, visible: true, editable: true, required: true },
        { key: 'failureMode', label: 'Failure Mode', width: 180, visible: true, editable: true, required: true },
        { key: 'missionPhase', label: 'Mission Phase', width: 100, visible: true, editable: true },
        { key: 'failureEffectLocal', label: 'Local Effect', width: 150, visible: true, editable: true },
        { key: 'failureEffectNext', label: 'Next Higher Effect', width: 150, visible: true, editable: true },
        { key: 'failureEffectEnd', label: 'End Effect', width: 150, visible: true, editable: true, required: true },
        { key: 'severityClass', label: 'Severity Class', width: 80, visible: true, editable: true, required: true },
        { key: 'failureProb', label: 'λp (Failure Rate)', width: 80, visible: true, editable: true },
        { key: 'failureModeRatio', label: 'α (Mode Ratio)', width: 80, visible: true, editable: true },
        { key: 'failureEffectProb', label: 'β (Effect Prob)', width: 80, visible: true, editable: true },
        { key: 'criticality', label: 'Cm (Criticality)', width: 80, visible: true, editable: false },
        { key: 'compensatingProvision', label: 'Compensating Provision', width: 180, visible: true, editable: true },
        { key: 'remarks', label: 'Remarks', width: 150, visible: true, editable: true },
    ],
    severityScale: [
        { value: 1, description: 'Category I - Catastrophic' },
        { value: 2, description: 'Category II - Critical' },
        { value: 3, description: 'Category III - Marginal' },
        { value: 4, description: 'Category IV - Minor' },
    ],
    occurrenceScale: [
        { value: 5, description: 'Level A - Frequent' },
        { value: 4, description: 'Level B - Reasonably probable' },
        { value: 3, description: 'Level C - Occasional' },
        { value: 2, description: 'Level D - Remote' },
        { value: 1, description: 'Level E - Extremely unlikely' },
    ],
    detectionScale: [
        { value: 5, description: 'No detection means' },
        { value: 4, description: 'Limited detection' },
        { value: 3, description: 'Partial detection' },
        { value: 2, description: 'Good detection' },
        { value: 1, description: 'Certain detection/prevention' },
    ],
    causeCategories: ['Design', 'Manufacturing', 'Maintenance', 'Operations', 'Environment'],
    complianceRequirements: ['MIL-STD-1629A', 'MIL-HDBK-217F', 'CMMC Level 2+'],
};

// ============================================================
// IEC 61508 / ISO 26262 (Functional Safety)
// Primary: Safety-Critical Systems
// ============================================================

const IEC_61508_CONFIG: StandardConfig = {
    id: 'IEC_61508',
    name: 'IEC 61508 / ISO 26262',
    description: 'Functional Safety for E/E/PE Systems and Automotive',
    industry: ['Automotive E/E', 'Industrial Automation', 'Railway', 'Nuclear'],
    useActionPriority: true,
    columns: [
        { key: 'systemElement', label: 'System Element', width: 120, visible: true, editable: true, required: true },
        { key: 'safetyFunction', label: 'Safety Function', width: 180, visible: true, editable: true, required: true },
        { key: 'silLevel', label: 'SIL/ASIL', width: 60, visible: true, editable: true },
        { key: 'failureMode', label: 'Failure Mode', width: 180, visible: true, editable: true, required: true },
        { key: 'failureMechanism', label: 'Failure Mechanism', width: 150, visible: true, editable: true },
        { key: 'safetyGoalViolation', label: 'Safety Goal Violation', width: 150, visible: true, editable: true, required: true },
        { key: 'severity', label: 'S', width: 50, visible: true, editable: true, required: true },
        { key: 'exposure', label: 'E', width: 50, visible: true, editable: true, required: true },
        { key: 'controllability', label: 'C', width: 50, visible: true, editable: true, required: true },
        { key: 'asil', label: 'ASIL', width: 60, visible: true, editable: false },
        { key: 'safetyMechanism', label: 'Safety Mechanism', width: 200, visible: true, editable: true },
        { key: 'diagnosticCoverage', label: 'DC%', width: 60, visible: true, editable: true },
        { key: 'mpf', label: 'MPF', width: 60, visible: true, editable: true },
        { key: 'status', label: 'Status', width: 100, visible: true, editable: true },
    ],
    severityScale: [
        { value: 3, description: 'S3 - Life-threatening, fatal' },
        { value: 2, description: 'S2 - Severe, survival probable' },
        { value: 1, description: 'S1 - Light/moderate injuries' },
        { value: 0, description: 'S0 - No injuries' },
    ],
    occurrenceScale: [
        { value: 4, description: 'E4 - High exposure' },
        { value: 3, description: 'E3 - Medium exposure' },
        { value: 2, description: 'E2 - Low exposure' },
        { value: 1, description: 'E1 - Negligible' },
    ],
    detectionScale: [
        { value: 3, description: 'C3 - Difficult to control' },
        { value: 2, description: 'C2 - Normal controllability' },
        { value: 1, description: 'C1 - Simply controllable' },
        { value: 0, description: 'C0 - Generally controllable' },
    ],
    causeCategories: ['Random HW Fault', 'Systematic Fault', 'CCF', 'External Interference', 'Software'],
    complianceRequirements: ['ISO 26262', 'IEC 61508', 'IEC 62443', 'ISO 21448 SOTIF'],
};

// ============================================================
// ISO 9001:2015 (Generic QMS)
// Primary: Cross-Industry
// ============================================================

const ISO_9001_CONFIG: StandardConfig = {
    id: 'ISO_9001',
    name: 'ISO 9001:2015',
    description: 'Quality Management System Risk-Based Thinking',
    industry: ['Manufacturing', 'Services', 'General Industry'],
    useActionPriority: false,
    columns: [
        { key: 'processStepNumber', label: 'Process ID', width: 80, visible: true, editable: true, required: true },
        { key: 'processStepName', label: 'Process', width: 180, visible: true, editable: true, required: true },
        { key: 'processFunction', label: 'Process Output', width: 200, visible: true, editable: true, required: true },
        { key: 'failureMode', label: 'Risk/Failure Mode', width: 180, visible: true, editable: true, required: true },
        { key: 'failureEffectEnd', label: 'Impact on Quality', width: 200, visible: true, editable: true, required: true },
        { key: 'severity', label: 'Impact', width: 60, visible: true, editable: true, required: true },
        { key: 'occurrence', label: 'Likelihood', width: 70, visible: true, editable: true, required: true },
        { key: 'detection', label: 'Detection', width: 70, visible: true, editable: true, required: true },
        { key: 'rpn', label: 'Risk Score', width: 70, visible: true, editable: false },
        { key: 'preventionControls', label: 'Risk Treatment', width: 200, visible: true, editable: true },
        { key: 'responsiblePerson', label: 'Owner', width: 120, visible: true, editable: true },
        { key: 'status', label: 'Status', width: 100, visible: true, editable: true },
    ],
    severityScale: [
        { value: 5, description: 'Critical - Major quality escape, recalls' },
        { value: 4, description: 'High - Significant customer complaint' },
        { value: 3, description: 'Medium - Customer notices issue' },
        { value: 2, description: 'Low - Minor internal issue' },
        { value: 1, description: 'Negligible - No real impact' },
    ],
    occurrenceScale: [
        { value: 5, description: 'Almost certain - >90%' },
        { value: 4, description: 'Likely - 50-90%' },
        { value: 3, description: 'Possible - 10-50%' },
        { value: 2, description: 'Unlikely - 1-10%' },
        { value: 1, description: 'Rare - <1%' },
    ],
    detectionScale: [
        { value: 5, description: 'Very difficult to detect' },
        { value: 4, description: 'Unlikely to detect' },
        { value: 3, description: 'May be detected' },
        { value: 2, description: 'Likely to detect' },
        { value: 1, description: 'Will be detected' },
    ],
    causeCategories: ['People', 'Process', 'Technology', 'External'],
    complianceRequirements: ['ISO 9001:2015', 'ISO 31000'],
};

// ============================================================
// IATF 16949 (Automotive Quality)
// Primary: Automotive Supply Chain
// ============================================================

const IATF_16949_CONFIG: StandardConfig = {
    id: 'IATF_16949',
    name: 'IATF 16949:2016',
    description: 'Automotive Quality Management System with core tools',
    industry: ['Automotive', 'Automotive Supply Chain'],
    useActionPriority: true,
    columns: [
        { key: 'processStepNumber', label: 'Op #', width: 60, visible: true, editable: true, required: true },
        { key: 'processStepName', label: 'Operation Description', width: 180, visible: true, editable: true, required: true },
        { key: 'machine', label: 'Machine/Device', width: 120, visible: true, editable: true },
        { key: 'processElement', label: '4M', width: 80, visible: true, editable: true },
        { key: 'processFunction', label: 'Characteristic', width: 150, visible: true, editable: true, required: true },
        { key: 'failureMode', label: 'Potential Failure Mode', width: 180, visible: true, editable: true, required: true },
        { key: 'failureEffectEnd', label: 'Potential Effect', width: 180, visible: true, editable: true, required: true },
        { key: 'severity', label: 'S', width: 40, visible: true, editable: true, required: true },
        { key: 'specialCharacteristic', label: 'Class', width: 50, visible: true, editable: true },
        { key: 'failureCause', label: 'Potential Cause', width: 180, visible: true, editable: true, required: true },
        { key: 'preventionControls', label: 'Prevention Control', width: 150, visible: true, editable: true },
        { key: 'occurrence', label: 'O', width: 40, visible: true, editable: true, required: true },
        { key: 'detectionControls', label: 'Detection Control', width: 150, visible: true, editable: true },
        { key: 'detection', label: 'D', width: 40, visible: true, editable: true, required: true },
        { key: 'actionPriority', label: 'AP', width: 50, visible: true, editable: false },
        { key: 'status', label: 'Status', width: 80, visible: true, editable: true },
    ],
    severityScale: AIAG_VDA_CONFIG.severityScale,
    occurrenceScale: AIAG_VDA_CONFIG.occurrenceScale,
    detectionScale: AIAG_VDA_CONFIG.detectionScale,
    causeCategories: ['Man', 'Machine', 'Material', 'Method', 'Measurement', 'Environment'],
    complianceRequirements: ['IATF 16949:2016', 'AIAG Core Tools', 'Customer Specific Requirements'],
};

// ============================================================
// EXPORT ALL STANDARDS
// ============================================================

export const STANDARD_CONFIGS: Record<PfmeaStandard, StandardConfig> = {
    'AIAG_VDA': AIAG_VDA_CONFIG,
    'AIAG_V4': AIAG_V4_CONFIG,
    'ISO_14971': ISO_14971_CONFIG,
    'AS9100D': AS9100D_CONFIG,
    'MIL_STD_1629A': MIL_STD_1629A_CONFIG,
    'IEC_61508': IEC_61508_CONFIG,
    'ISO_9001': ISO_9001_CONFIG,
    'IATF_16949': IATF_16949_CONFIG,
};

// ============================================================
// CALCULATION FUNCTIONS
// ============================================================


// ============================================================
// AIAG-VDA ACTION PRIORITY (AP) DECISION TABLES
// Based on AIAG-VDA FMEA Handbook 1st Edition (2019)
// Official tables from pages 89-91
// ============================================================

// Table 1: For Severity 9-10 (Safety or Compliance Critical)
const AP_TABLE_S9_10: Record<number, Record<number, 'H' | 'M' | 'L'>> = {
    10: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'H', 5: 'H', 4: 'H', 3: 'H', 2: 'H', 1: 'H' },
    9: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'H', 5: 'H', 4: 'H', 3: 'H', 2: 'M', 1: 'M' },
    8: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'H', 5: 'H', 4: 'H', 3: 'M', 2: 'M', 1: 'M' },
    7: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'H', 5: 'H', 4: 'H', 3: 'M', 2: 'M', 1: 'M' },
    6: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'H', 5: 'H', 4: 'M', 3: 'M', 2: 'M', 1: 'M' },
    5: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'H', 5: 'H', 4: 'M', 3: 'M', 2: 'M', 1: 'M' },
    4: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'M', 5: 'M', 4: 'M', 3: 'M', 2: 'M', 1: 'M' },
    3: { 10: 'H', 9: 'H', 8: 'H', 7: 'M', 6: 'M', 5: 'M', 4: 'M', 3: 'M', 2: 'M', 1: 'M' },
    2: { 10: 'H', 9: 'M', 8: 'M', 7: 'M', 6: 'M', 5: 'M', 4: 'M', 3: 'M', 2: 'M', 1: 'M' },
    1: { 10: 'H', 9: 'M', 8: 'M', 7: 'M', 6: 'M', 5: 'M', 4: 'M', 3: 'M', 2: 'M', 1: 'M' },
};

// Table 2: For Severity 7-8 (High Severity)
const AP_TABLE_S7_8: Record<number, Record<number, 'H' | 'M' | 'L'>> = {
    10: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'H', 5: 'H', 4: 'H', 3: 'M', 2: 'M', 1: 'L' },
    9: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'H', 5: 'H', 4: 'M', 3: 'M', 2: 'M', 1: 'L' },
    8: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'H', 5: 'M', 4: 'M', 3: 'M', 2: 'M', 1: 'L' },
    7: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'M', 5: 'M', 4: 'M', 3: 'M', 2: 'L', 1: 'L' },
    6: { 10: 'H', 9: 'H', 8: 'H', 7: 'M', 6: 'M', 5: 'M', 4: 'M', 3: 'M', 2: 'L', 1: 'L' },
    5: { 10: 'H', 9: 'H', 8: 'M', 7: 'M', 6: 'M', 5: 'M', 4: 'M', 3: 'L', 2: 'L', 1: 'L' },
    4: { 10: 'H', 9: 'M', 8: 'M', 7: 'M', 6: 'M', 5: 'M', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    3: { 10: 'M', 9: 'M', 8: 'M', 7: 'M', 6: 'M', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    2: { 10: 'M', 9: 'M', 8: 'M', 7: 'M', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    1: { 10: 'M', 9: 'M', 8: 'M', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
};

// Table 3: For Severity 4-6 (Moderate Severity)
const AP_TABLE_S4_6: Record<number, Record<number, 'H' | 'M' | 'L'>> = {
    10: { 10: 'H', 9: 'H', 8: 'H', 7: 'H', 6: 'M', 5: 'M', 4: 'M', 3: 'L', 2: 'L', 1: 'L' },
    9: { 10: 'H', 9: 'H', 8: 'H', 7: 'M', 6: 'M', 5: 'M', 4: 'M', 3: 'L', 2: 'L', 1: 'L' },
    8: { 10: 'H', 9: 'H', 8: 'M', 7: 'M', 6: 'M', 5: 'M', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    7: { 10: 'H', 9: 'M', 8: 'M', 7: 'M', 6: 'M', 5: 'M', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    6: { 10: 'M', 9: 'M', 8: 'M', 7: 'M', 6: 'M', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    5: { 10: 'M', 9: 'M', 8: 'M', 7: 'M', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    4: { 10: 'M', 9: 'M', 8: 'M', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    3: { 10: 'M', 9: 'M', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    2: { 10: 'M', 9: 'L', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    1: { 10: 'L', 9: 'L', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
};

// Table 4: For Severity 1-3 (Low Severity)
const AP_TABLE_S1_3: Record<number, Record<number, 'H' | 'M' | 'L'>> = {
    10: { 10: 'M', 9: 'M', 8: 'M', 7: 'M', 6: 'M', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    9: { 10: 'M', 9: 'M', 8: 'M', 7: 'M', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    8: { 10: 'M', 9: 'M', 8: 'M', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    7: { 10: 'M', 9: 'M', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    6: { 10: 'M', 9: 'L', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    5: { 10: 'L', 9: 'L', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    4: { 10: 'L', 9: 'L', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    3: { 10: 'L', 9: 'L', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    2: { 10: 'L', 9: 'L', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
    1: { 10: 'L', 9: 'L', 8: 'L', 7: 'L', 6: 'L', 5: 'L', 4: 'L', 3: 'L', 2: 'L', 1: 'L' },
};

/**
 * Calculate Action Priority using official AIAG-VDA 2019 decision tables
 * @param s Severity rating (1-10)
 * @param o Occurrence rating (1-10)
 * @param d Detection rating (1-10)
 * @returns Action Priority: 'H' (High), 'M' (Medium), or 'L' (Low)
 */
export function calculateActionPriority(s: number, o: number, d: number): 'H' | 'M' | 'L' {
    // Validate inputs
    if (s < 1 || s > 10 || o < 1 || o > 10 || d < 1 || d > 10) {
        console.warn(`Invalid S/O/D values: S=${s}, O=${o}, D=${d}. Defaulting to 'L'`);
        return 'L';
    }

    // Select appropriate table based on Severity
    let table: Record<number, Record<number, 'H' | 'M' | 'L'>>;

    if (s >= 9) {
        table = AP_TABLE_S9_10;
    } else if (s >= 7) {
        table = AP_TABLE_S7_8;
    } else if (s >= 4) {
        table = AP_TABLE_S4_6;
    } else {
        table = AP_TABLE_S1_3;
    }

    // Lookup AP value
    return table[o]?.[d] || 'L';
}


export function calculateRPN(s: number, o: number, d: number): number {
    return s * o * d;
}

export function getIndustryStandards(industry: string): StandardConfig[] {
    return Object.values(STANDARD_CONFIGS).filter(config =>
        config.industry.some(ind => ind.toLowerCase().includes(industry.toLowerCase()))
    );
}

export function getComplianceRequirements(standardId: PfmeaStandard): string[] {
    return STANDARD_CONFIGS[standardId]?.complianceRequirements || [];
}
